import {
  Sun, Wind, Zap, Thermometer, Atom, Flame, Map, ListTree, FileText, Shield, Camera,
  Milestone, MessageSquare, TrendingUp, LayoutDashboard, Users, ClipboardCheck, FileCheck,
  Truck, GitBranch, Package, Clock, FolderTree, ScrollText, History, Lock,
} from "lucide-react";

const LIFECYCLE = ["Pre-construction", "Mobilisation", "Execution", "Testing & commissioning", "Handover"];

export const INDUSTRIES = {
  "solar-epc": {
    icon: Sun,
    name: "Solar EPC",
    meta: {
      title: "Solar EPC Project Management Software India | Plan-Sarthi",
      description:
        "Solar EPC project management software for Indian developers and contractors. Track land survey, module procurement, grid synchronisation, DPR and commissioning snags in one platform. 14-day free trial.",
    },
    hero: "https://images.unsplash.com/photo-1723133371535-1412bc2e412e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxzb2xhciUyMGZhcm0lMjBzdW5yaXNlJTIwYWVyaWFsfGVufDB8fHx8MTc4Nzk5ODc2NHww&ixlib=rb-4.1.0&q=85&w=1920",
    tagline: "From land survey to grid synchronisation — manage every phase of your solar project in one platform.",
    intro:
      "Utility-scale solar lives and dies by throughput: modules mounted per day, strings terminated, trenches dug. Plan-Sarthi measures that throughput against your PPA date and tells you, weeks in advance, whether you will make it.",
    challenges: [
      ["Land acquisition and survey delays", "Right-of-way disputes and pending revenue records stall mobilisation while the PPA clock keeps running."],
      ["Module and inverter procurement tracking", "Container-wise arrivals, DC capacity received versus installed, and BOS shortages that surface only at the array."],
      ["Grid connectivity timelines", "Bay extension, CTU/STU approvals and evacuation readiness sit outside your control but inside your schedule."],
      ["Commissioning snags and punch lists", "Thousands of small defects across blocks with no single register, so PAC slips by weeks."],
    ],
    features: [
      [ListTree, "WBS built for solar phases", "Survey, piling, MMS, module mounting, DC/AC cabling, ICR, ITP and grid tie-in as real quantities — piles driven, modules mounted, RMT of cable pulled — not vague percentages."],
      [FileText, "BOQ parser for panel and inverter quantities", "Drop in the vendor BOQ or estimate; modules, structures, inverters and cable schedules become live scope with units normalised to Nos, MT and RMT."],
      [Shield, "Risk Radar for weather and grid risk", "Trailing run-rates against the balance scope forecast each block's finish date and flag which array is going to miss synchronisation."],
      [Camera, "DPR with site photo evidence", "Block-wise daily progress from a phone — even with no signal — with manpower split, idle hours and weather logged against the same activity."],
      [Milestone, "Block-wise commissioning register", "String testing, IV curves and snag closure tracked per block so PAC documentation assembles itself."],
      [LayoutDashboard, "Multi-block S-curve", "Planned versus actual MW installed, with earned value on real executed DC capacity."],
    ],
    metrics: [
      ["Up to 30%", "faster snag closure with a single block-wise punch register"],
      ["Daily", "MW-installed visibility instead of a Monday spreadsheet"],
      ["Weeks earlier", "warning on blocks that will miss grid synchronisation"],
    ],
    lifecycle: LIFECYCLE,
    testimonial: {
      quote: "We used to discover a slipping block at the monthly review. Now the trend shows up while there is still time to add crews.",
      name: "Project Head",
      designation: "Solar EPC Division",
      company: "150 MW utility-scale project",
    },
  },

  "wind-epc": {
    icon: Wind,
    name: "Wind EPC",
    meta: {
      title: "Wind EPC Project Management Software India | Plan-Sarthi",
      description:
        "Wind EPC project management software to track turbine erection, blade logistics, foundation pours, substation coordination and remote-site DPRs across multiple wind sites. Free 14-day trial.",
    },
    hero: "https://images.unsplash.com/photo-1592182739012-129026a7c8c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHx3aW5kJTIwdHVyYmluZXMlMjByaWRnZSUyMGR1c2t8ZW58MHx8fHwxNzg3OTk4NzY0fDA&ixlib=rb-4.1.0&q=85&w=1920",
    tagline: "Track turbine erection, blade logistics, and power evacuation — all on one dashboard.",
    intro:
      "Wind sites are remote, weather-bound and crane-constrained. One blade stuck at a ghat section can idle a ₹2-crore crane for a week. Plan-Sarthi keeps that sequence visible from the head office.",
    challenges: [
      ["Blade and nacelle transport delays", "Over-dimensional consignments, ghat sections and police permissions decide your erection window, not your plan."],
      ["Foundation pour sequencing", "Curing periods and crane pad readiness must line up with the crane's arrival or the whole cycle slips."],
      ["Substation and evacuation coordination", "Pooling station, line stringing and charging run on a different agency's calendar."],
      ["Remote site reporting", "Sites with no connectivity report by phone call, so the head office is always working on yesterday's picture."],
    ],
    features: [
      [Milestone, "Milestone tracker for erection phases", "Foundation, tower sections, nacelle, hub and blades tracked WTG by WTG with dates and float, so the crane path is planned, not improvised."],
      [MessageSquare, "Field reporting from remote sites", "DPRs logged offline on the device and synced the moment a bar of signal returns — nothing is lost on a hilltop."],
      [TrendingUp, "Predictive commissioning forecast", "Erection cycle time per WTG turns into a forecast charging date you can defend with the offtaker."],
      [LayoutDashboard, "Multi-site dashboard", "Every wind site in one portfolio view — MW erected, MW charged, and which site is dragging the programme."],
      [Truck, "Logistics and consignment hindrances", "Transport delays logged as priced hindrances against the affected WTG, ready for an extension-of-time claim."],
      [Shield, "Weather and crane-idling exposure", "Idle crane hours costed automatically so the commercial impact of a weather window is visible immediately."],
    ],
    metrics: [
      ["Per-WTG", "erection cycle time, measured instead of estimated"],
      ["Zero data loss", "on sites with no mobile coverage"],
      ["One view", "across every wind site in the portfolio"],
    ],
    lifecycle: LIFECYCLE,
    testimonial: {
      quote: "The crane schedule finally matches the foundation reality. That alone paid for the platform.",
      name: "Construction Manager",
      designation: "Wind Projects",
      company: "Multi-site 100 MW+ programme",
    },
  },

  "engineering-epc": {
    icon: Zap,
    name: "Engineering EPC",
    meta: {
      title: "EPC Project Management Software for Engineering Contractors India | Plan-Sarthi",
      description:
        "End-to-end EPC project controls for multi-discipline engineering projects — civil, structural, mechanical, piping, electrical and instrumentation. WBS, BOQ, EVM, critical path and subcontractor access.",
    },
    hero: "https://images.unsplash.com/photo-1784911545613-391bb6853d00?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxvaWwlMjByZWZpbmVyeSUyMGluZHVzdHJpYWwlMjBwbGFudCUyMGNvbnN0cnVjdGlvbnxlbnwwfHx8fDE3ODc5OTg3NjN8MA&ixlib=rb-4.1.0&q=85&w=1920",
    tagline: "End-to-end EPC execution management for complex multi-discipline projects.",
    intro:
      "Refineries, petrochemical units and process plants fail at the interfaces — civil to structural, structural to piping, piping to E&I. Plan-Sarthi holds every discipline on one WBS so the handover between them is scheduled, not negotiated.",
    challenges: [
      ["Multi-discipline coordination", "Six disciplines, six planners, six spreadsheets — and no single view of who is holding whom."],
      ["Vendor and subcontractor management", "Dozens of agencies reporting in different formats, with quantities nobody reconciles until billing."],
      ["Change order tracking", "Scope creep absorbed silently at site and argued about months later without records."],
      ["Schedule compression", "When the client pulls the date in, you need to know which activities actually move the completion date."],
    ],
    features: [
      [FolderTree, "Multi-discipline WBS", "One three-level breakdown with discipline-wise rollups — Cu.M, MT, Dia-Inch, RMT — so every agency reports into the same structure."],
      [FileText, "BOQ parser for bulk material", "Structural steel, piping and cable schedules imported from Excel or PDF, units normalised, confidence-scored, reviewed and confirmed by your planner."],
      [Users, "Role-based subcontractor access", "Agencies see and update only their own fronts. Viewer, engineer, PM, HOD and admin permissions are enforced in the database, not just hidden in the UI."],
      [GitBranch, "Critical path with defensible float", "Forward and backward pass gives ES/EF/LS/LF and total float, so compression decisions target the activities that actually matter."],
      [Shield, "Risk Radar on critical-path fronts", "Value at risk, forecast overrun and an AI-written brief naming causes and recovery actions."],
      [ScrollText, "Priced hindrance register", "Every delay attributed, dated and costed against the affected activity — the evidence a change order needs."],
    ],
    metrics: [
      ["6 disciplines", "on one breakdown structure and one S-curve"],
      ["Every claim", "backed by a dated, priced hindrance record"],
      ["Real float", "instead of a percentage guess"],
    ],
    lifecycle: LIFECYCLE,
    testimonial: {
      quote: "For the first time, civil and piping argue about the same numbers instead of two different spreadsheets.",
      name: "Planning Manager",
      designation: "EPC Contracts",
      company: "Process plant package",
    },
  },

  hvac: {
    icon: Thermometer,
    name: "HVAC",
    meta: {
      title: "HVAC Project Management Software for Contractors India | Plan-Sarthi",
      description:
        "HVAC installation project management software — equipment lead-time tracking, duct fabrication sequencing, checklist DPRs, commissioning documentation and BOQ control for data centres and hospitals.",
    },
    hero: "https://images.unsplash.com/photo-1615309662243-70f6df917b59?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwSFZBQyUyMGR1Y3RpbmclMjBpbnN0YWxsYXRpb258ZW58MHx8fHwxNzg3OTk4NzY0fDA&ixlib=rb-4.1.0&q=85&w=1920",
    tagline: "Precision scheduling and compliance tracking for HVAC installation projects.",
    intro:
      "Data centres and hospitals do not forgive a missed commissioning date, and they do not accept an undocumented one. Plan-Sarthi tracks the equipment, the fabrication and the paperwork on the same schedule.",
    challenges: [
      ["Equipment lead times", "Chillers, AHUs and VRF units ordered months ahead; one slipped delivery re-sequences the entire installation."],
      ["Duct fabrication sequencing", "Shop fabrication must stay ahead of site erection floor by floor, or crews stand idle."],
      ["Commissioning documentation", "TAB reports, ITPs and certificates scattered across email when the client asks for the handover file."],
      ["Regulatory and code compliance", "Fire, IAQ and energy-code checks that must be evidenced, not just done."],
    ],
    features: [
      [Truck, "Equipment delivery tracker in the WBS", "Ordered, dispatched, received and installed as milestones on the activity itself, so a lead-time slip immediately shows its schedule impact."],
      [ClipboardCheck, "Checklist-based DPR", "Floor-wise and zone-wise daily progress with pre-installation and pre-commissioning checklists completed at site on a phone."],
      [FileCheck, "Commissioning document control", "TAB reports, test certificates and ITP sign-offs held against the activity they belong to, versioned and ready for handover."],
      [FileText, "BOQ for duct and equipment quantities", "Sq.M of ducting, insulation, piping RMT and equipment Nos imported and tracked against installed quantity."],
      [Clock, "Zone-wise progress and idle-hour tracking", "Where crews waited, for how long, and what it cost."],
      [Shield, "Forecast on commissioning readiness", "Trend-based forecast of when each zone will actually be ready for testing."],
    ],
    metrics: [
      ["Zone-wise", "readiness instead of a single project percentage"],
      ["Handover-ready", "documentation assembled as work progresses"],
      ["Lead-time slips", "visible as schedule impact the day they happen"],
    ],
    lifecycle: LIFECYCLE,
    testimonial: {
      quote: "The handover file used to take three weeks to assemble. Now it is a click, because the documents were filed against the activity all along.",
      name: "MEP Lead",
      designation: "HVAC Projects",
      company: "Data centre installation",
    },
  },

  "nuclear-projects": {
    icon: Atom,
    name: "Nuclear Projects",
    meta: {
      title: "Nuclear Project Management & Audit-Ready Project Controls India | Plan-Sarthi",
      description:
        "Audit-ready project controls for nuclear construction packages — full edit audit trail, permission logs, milestone sign-off workflows, quality hold points and document version control.",
    },
    hero: "https://images.unsplash.com/photo-1630142895963-6996ae6b3a5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxudWNsZWFyJTIwcG93ZXIlMjBwbGFudCUyMGNvb2xpbmclMjB0b3dlcnN8ZW58MHx8fHwxNzg3OTk4NzYzfDA&ixlib=rb-4.1.0&q=85&w=1920",
    tagline: "Audit-ready project controls for India's most complex energy projects.",
    intro:
      "On a nuclear package the question is never only \"what is the progress\" — it is \"who recorded it, when, against which hold point, and can you prove it\". Plan-Sarthi is built so that answer is always one click away.",
    challenges: [
      ["Extreme regulatory compliance", "AERB and client QA expect traceability on every quantity, every sign-off and every revision."],
      ["Multi-agency coordination", "Owner, consultant, QA, contractor and vendors all touching the same hold points."],
      ["Long project lifecycles", "Teams rotate over eight to ten years; institutional memory must live in the system, not in people."],
      ["Quality hold points", "Work cannot proceed past a stage without inspection and recorded clearance."],
    ],
    features: [
      [History, "Full audit trail on every edit", "Timestamp, user, field, old value, new value and a mandatory reason. The log is append-only — even a correction is itself recorded."],
      [Lock, "Role-based access with permission logs", "Six roles enforced at the database layer, with a 24-hour supervised revert window on administrative corrections."],
      [ClipboardCheck, "Milestone sign-off workflows", "DPR entries stay unvalidated until an authorised person clears them, so no figure counts until it is signed."],
      [FileCheck, "Document version control", "Drawings, ITPs, WPS and clearance certificates versioned against the activity and the hold point they govern."],
      [GitBranch, "Hold-point aware critical path", "Inspection stages modelled as activities with float, so a pending clearance shows its true schedule cost."],
      [ScrollText, "Defensible delay records", "Every hindrance attributed and priced at the time it occurred, not reconstructed a year later."],
    ],
    metrics: [
      ["100%", "of administrative edits logged with user, time and reason"],
      ["Append-only", "audit log with no delete path, by design"],
      ["8–10 year", "traceability that survives team rotation"],
    ],
    badge: "Audit Trail Enabled — Every Edit Logged with Timestamp and User",
    lifecycle: ["Pre-construction & licensing", "Mobilisation", "Execution with hold points", "Testing & commissioning", "Handover & O&M"],
    testimonial: {
      quote: "When QA asks who changed a quantity and why, we answer in seconds. That is the difference between an audit and an argument.",
      name: "QA & Planning Head",
      designation: "Nuclear Construction Package",
      company: "Containment structure works",
    },
  },

  "thermal-power-plant": {
    icon: Flame,
    name: "Thermal Power Plant",
    meta: {
      title: "Thermal Power Plant Project Management Software India | Plan-Sarthi",
      description:
        "Thermal power plant construction management — boiler and turbine erection sequencing, coal handling coordination, shift-wise DPR, grid synchronisation forecasting and O&M handover documentation.",
    },
    hero: "https://images.unsplash.com/photo-1708053480782-87e6044190df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHx0aGVybWFsJTIwcG93ZXIlMjBwbGFudCUyMGNvb2xpbmclMjB0b3dlcnMlMjBnb2xkZW4lMjBob3VyfGVufDB8fHx8MTc4Nzk5ODc2M3ww&ixlib=rb-4.1.0&q=85&w=1920",
    tagline: "From boiler erection to synchronisation — keep your thermal project on schedule and on budget.",
    intro:
      "A thermal package is thousands of tonnes of steel erected in a fixed sequence across three shifts. Plan-Sarthi measures MT erected per shift and converts it into a synchronisation date you can commit to.",
    challenges: [
      ["Boiler and turbine erection sequencing", "Pressure parts, structures and TG deck follow a strict order where one late consignment cascades for months."],
      ["Coal handling and BOP coordination", "CHP, ash handling, water systems and switchyard run as near-independent packages that must converge."],
      ["Grid synchronisation delays", "Charging, trial run and COD depend on agencies outside your control but inside your liquidated damages."],
      ["O&M handover documentation", "Punch lists, test reports and as-built records demanded in full at the worst possible moment."],
    ],
    features: [
      [ListTree, "Phase-wise WBS for thermal activities", "Foundations, structures, boiler pressure parts, TG, piping, CHP and switchyard as real quantities — MT erected, Cu.M poured, Dia-Inch welded."],
      [FileText, "BOQ for structural and mechanical packages", "Bulk steel, piping and equipment schedules imported and tracked against erected quantity, not invoice quantity."],
      [Shield, "Risk Radar for critical equipment delays", "Forecast finish per package with value at risk, flagging the equipment that will move COD."],
      [Clock, "Shift-wise DPR tracking", "Three shifts a day captured separately with manpower split and idle hours, so productivity per shift is visible."],
      [TrendingUp, "Synchronisation forecast", "Trailing erection rates project the charging and trial-run dates with a stated confidence level."],
      [FileCheck, "Punch list and handover register", "Snags and test reports tracked to closure per system, so O&M handover is assembled progressively."],
    ],
    metrics: [
      ["MT per shift", "erection productivity, measured across all three shifts"],
      ["Package-wise", "forecast of the date that actually drives COD"],
      ["Idle hours", "priced automatically as cost leakage"],
    ],
    lifecycle: ["Pre-construction", "Mobilisation & foundations", "Erection", "Testing, charging & trial run", "COD & O&M handover"],
    testimonial: {
      quote: "Boiler erection tonnage per shift is now a number we manage, not a number we discover at month end.",
      name: "Site Project Manager",
      designation: "Thermal Package",
      company: "2 × 660 MW project",
    },
  },

  "other-infrastructure": {
    icon: Map,
    name: "Other Infrastructure",
    meta: {
      title: "Infrastructure Project Management Software India — Roads, Bridges, Metro | Plan-Sarthi",
      description:
        "Project management software for roads, bridges, ports and metro rail. Multi-location tracking, WBS templates, government BOQ import, milestone billing documentation and field alerts.",
    },
    hero: "https://images.unsplash.com/photo-1708357086795-7f36fb996c35?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxoaWdod2F5JTIwYnJpZGdlJTIwY29uc3RydWN0aW9uJTIwYWVyaWFsJTIwaW5kaWF8ZW58MHx8fHwxNzg3OTk4NzY0fDA&ixlib=rb-4.1.0&q=85&w=1920",
    tagline: "Roads, bridges, ports, metros — Plan-Sarthi adapts to every infrastructure project type.",
    intro:
      "Linear projects spread the same crew across 40 kilometres. Plan-Sarthi tracks chainage-wise progress, keeps every stretch on one S-curve and turns milestone billing into a by-product of the daily report.",
    challenges: [
      ["Multi-location and chainage tracking", "Work fronts spread over tens of kilometres, each with its own crew, plant and progress."],
      ["Government approval delays", "Utility shifting, forest and NHAI/PWD clearances that hold up stretches for months."],
      ["Subcontractor coordination", "Earthwork, GSB, DBM, BC, structures and drainage often with a different agency per stretch."],
      ["Milestone billing documentation", "RA bills that need measured quantities, dates and evidence in the client's format."],
    ],
    features: [
      [LayoutDashboard, "Multi-project, multi-stretch dashboard", "Every package and stretch in one portfolio view with progress, EVM and forecast side by side."],
      [FolderTree, "WBS templates for roads and bridges", "Ready structures for earthwork, pavement layers, culverts, VUPs and bridge substructure and superstructure."],
      [MessageSquare, "Field alerts and offline reporting", "Site engineers log chainage-wise progress from the field, offline if needed, and it syncs automatically."],
      [FileText, "BOQ import from government estimate formats", "NHAI, PWD and departmental estimate sheets parsed into a live WBS with units normalised."],
      [ScrollText, "Approval and hindrance register", "Utility shifting, land and clearance delays recorded per chainage with cost and time impact for the claim file."],
      [Package, "Milestone billing support", "Executed quantities and dates roll straight into the RA-bill pack, with the S-curve and measurement basis attached."],
    ],
    metrics: [
      ["Chainage-wise", "progress instead of a single project percentage"],
      ["Every stretch", "on one S-curve and one earned-value view"],
      ["RA bills", "supported by measured, dated quantities"],
    ],
    lifecycle: ["Pre-construction & approvals", "Mobilisation", "Execution by stretch", "Testing & quality", "Handover & DLP"],
    testimonial: {
      quote: "Forty kilometres, nine subcontractors, one dashboard. The RA bill now takes days instead of weeks.",
      name: "Project Director",
      designation: "Highways Division",
      company: "4-lane highway package",
    },
  },
};

export const INDUSTRY_LIST = Object.entries(INDUSTRIES).map(([slug, v]) => ({
  slug, name: v.name, icon: v.icon, tagline: v.tagline,
}));
